import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { breakpoints } from 'theme';

/** Aligns with profile Content (dew) */
export const CheckoutBackdrop = styled.div`
  background: ${({ theme }) => theme.color.dew};
  min-height: 100svh;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
`;

/** Matches profile subscription header title next to back control */
export const CheckoutHeaderTitle = styled.span`
  margin-left: 14px;
  font-weight: 600;

  color: ${({ theme }) => theme.color.pickledBluewood};
`;

export const CheckoutPageWrap = styled.div`
  max-width: 820px;
  width: 100%;
  margin: 0 auto;
  padding: 24px 25px 56px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.color.pickledBluewood};

  @media ${breakpoints.downSmPlus} {
    padding: 16px 16px 40px;
  }
`;

export const EnterpriseCheckoutPageWrap = styled(CheckoutPageWrap)`
  max-width: 900px;
`;

export const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 380px);
  gap: 24px;
  align-items: start;
  margin-bottom: 0;

  @media ${breakpoints.downMdPlus} {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }

  @media ${breakpoints.downSmPlus} {
    gap: 12px;
  }
`;

/** Wider two-column layout for Enterprise cards on large viewports; stacks to one column below mdPlus. */
export const EnterpriseCheckoutLayoutGrid = styled(CheckoutGrid)`
  grid-template-columns: minmax(0, 1fr) minmax(320px, 420px);
  gap: 28px;
`;

export const OrderCard = styled.div`
  background: ${({ theme }) => theme.color.white};
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  border-radius: 20px;
  text-align: left;
  padding: 24px;
  margin-bottom: 0;
  box-sizing: border-box;
  min-width: 0;

  @media ${breakpoints.downSmPlus} {
    padding: 16px;
    border-radius: 16px;
  }
`;

export const OrderCardTitle = styled.h2`
  font-size: 16px;
  font-weight: bold;
  color: ${({ theme }) => theme.color.pickledBluewood};
  margin: 0 0 18px;
`;

export const PlanSummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
  min-width: 0;

  @media ${breakpoints.downSmPlus} {
    gap: 12px;
    margin-bottom: 16px;
  }
`;

export const PlanIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${({ theme }) => theme.color.aquaSqueeze};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

export const PlanInfoName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
`;

export const PlanInfoSub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.baliHai};
  margin-top: 2px;
`;

export const PlanBadge = styled.span<{
  $variant: 'current' | 'new' | 'enterprise' | 'upgrade' | 'downgrade';
}>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 40px;
  margin-top: 5px;

  ${({ theme, $variant }) =>
    $variant === 'current'
      ? `
    background: ${theme.color.aquaSqueeze};
    color: ${theme.color.skobeloffGreen};
  `
      : $variant === 'enterprise'
      ? `
    background: ${theme.color.skobeloffGreen};
    color: #fff;
  `
      : $variant === 'upgrade'
      ? `
    background: ${theme.color.koromiko}33;
    color: ${theme.color.BlueBayoux};
  `
      : $variant === 'downgrade'
      ? `
    background: #fff0f0;
    color: #9b1c1c;
  `
      : `
    background: #f3f0ff;
    color: #5340b7;
  `}
`;

export const PlanBadgeSkeleton = styled.span`
  display: inline-block;
  width: 9.5rem;
  height: 22px;
  margin-top: 5px;
  border-radius: 40px;
  vertical-align: middle;
`;

export const SpecPills = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 20px;
`;

export const SpecPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: ${({ theme }) => theme.color.aquaSqueeze};
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  border-radius: 40px;
  padding: 5px 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  font-weight: 500;
`;

export const SpecPillVal = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
`;

export const PriceBreakdown = styled.div`
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
  padding-top: 16px;
`;

export const PbRow = styled.div<{ $highlight?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 13px;
  padding: 5px 0;

  @media ${breakpoints.downSmPlus} {
    font-size: 12px;
  }

  ${({ theme, $highlight }) =>
    $highlight &&
    `
    background: ${theme.color.aquaSqueeze};
    border-radius: 6px;
    padding: 6px 10px;
    margin: 2px -10px;

    span:first-child {
      color: ${theme.color.skobeloffGreen};
      font-weight: 500;
    }
    span:last-child {
      color: ${theme.color.skobeloffGreen};
      font-weight: 700;
    }
  `}
`;

export const PbLabelCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const PbLabel = styled.span`
  color: ${({ theme }) => theme.color.BlueBayoux};
`;

export const PbSubtext = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const PbVal = styled.span`
  color: ${({ theme }) => theme.color.pickledBluewood};
  font-weight: 500;
`;

export const PbValCredit = styled(PbVal)`
  color: ${({ theme }) => theme.color.brightTurquoise};
  font-weight: 700;
`;

export const PbDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.color.porcelain};
  margin: 10px 0;
`;

export const PbRowTotal = styled(PbRow)`
  font-size: 15px;
  margin-top: 4px;

  ${PbLabel} {
    color: ${({ theme }) => theme.color.pickledBluewood};
    font-weight: 600;
  }

  ${PbVal} {
    font-size: 19px;
    font-weight: 700;
  }
`;

export const IncludedSection = styled.div`
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
`;

export const IncludedTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${({ theme }) => theme.color.baliHai};
  margin-bottom: 10px;
`;

export const IncludedList = styled.ul`
  list-style: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 12px;
  margin: 0;
  padding: 0;

  @media ${breakpoints.downSmPlus} {
    grid-template-columns: 1fr;
  }
`;

export const IncludedItem = styled.li`
  font-size: 12px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  display: flex;
  gap: 6px;
  align-items: flex-start;
  line-height: 1.4;
  padding: 3px 0;
`;

export const IncludedCheck = styled.span`
  color: ${({ theme }) => theme.color.brightTurquoise};
  font-size: 13px;
  flex-shrink: 0;
  margin-top: 1px;
`;

export const InfoBox = styled.div`
  background: ${({ theme }) => theme.color.aquaSqueeze};
  border: 1px solid ${({ theme }) => theme.color.botticelli};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.skobeloffGreen};
  line-height: 1.55;
  margin-top: 14px;
  display: flex;
  gap: 8px;
`;

export const TrustRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

export const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const PaymentMethodSection = styled.div`
  margin-bottom: 20px;
`;

export const PaymentMethodLegend = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.color.baliHai};
  margin-bottom: 12px;
`;

export const PaymentMethodGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;

  @media ${breakpoints.downSmPlus} {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }
`;

export const PaymentMethodCard = styled.button<{
  $selected?: boolean;
  $disabled?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  width: 100%;
  min-width: 0;
  height: 100%;
  padding: 0;
  border-radius: 14px;
  border: 1.5px solid
    ${({ theme, $selected, $disabled }) =>
      $disabled
        ? theme.color.porcelain
        : $selected
        ? theme.color.skobeloffGreen
        : theme.color.porcelain};
  background: ${({ theme, $disabled }) =>
    $disabled ? theme.color.aquaSqueeze : theme.color.white};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s, opacity 0.15s;
  box-sizing: border-box;
  overflow: hidden;
  opacity: ${({ $disabled }) => ($disabled ? 0.62 : 1)};

  &:hover {
    border-color: ${({ theme, $disabled }) =>
      $disabled ? theme.color.porcelain : theme.color.casper};
  }

  ${({ $selected, $disabled, theme }) =>
    $selected &&
    !$disabled &&
    `
    box-shadow: 0 0 0 1px ${theme.color.skobeloffGreen}33;
  `}

  &:disabled {
    cursor: not-allowed;
  }
`;

export const PaymentMethodCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  flex-shrink: 0;
`;

export const PaymentBrandStrip = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
  padding: 8px 8px 10px;
  background: ${({ theme }) => theme.color.aquaSqueeze};
  min-height: 56px;
`;

export const PaymentBrandRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 8px;

  svg {
    width: auto;
    height: 20px;
    flex-shrink: 0;
    display: block;
  }
`;

export const InvoiceOptionStrip = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
  padding: 8px 8px 10px;
  background: ${({ theme }) => theme.color.blackSqueeze};
  min-height: 56px;
`;

export const PaymentMethodRadioUi = styled.span<{ $checked?: boolean }>`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid
    ${({ theme, $checked }) =>
      $checked ? theme.color.skobeloffGreen : theme.color.porcelain};
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme, $checked }) =>
      $checked ? theme.color.skobeloffGreen : 'transparent'};
  }
`;

export const PaymentMethodName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};
  line-height: 1.2;
`;

export const PaymentMethodDesc = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.color.baliHai};
  line-height: 1.2;
`;

export const PaymentMethodText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const InvoicePriceTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
  letter-spacing: -0.02em;
  line-height: 1.2;

  @media ${breakpoints.downSmPlus} {
    font-size: 20px;
  }
`;

export const InvoicePriceSub = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  margin-top: 8px;
  line-height: 1.5;
`;

export const AmberInfoBox = styled.div`
  background: ${({ theme }) => theme.color.koromiko}24;
  border: 1px solid ${({ theme }) => theme.color.koromiko};
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  line-height: 1.55;
  margin-top: 14px;
  text-align: left;
`;

/** Invoice + monthly billing: single prominent notice (annual required) */
export const InvoiceYearlyOnlyBanner = styled.div`
  background: ${({ theme }) => theme.color.koromiko}55;
  border: 2px solid ${({ theme }) => theme.color.koromiko};
  border-radius: 12px;
  padding: 16px 18px;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};
  line-height: 1.5;
  margin-top: 14px;
  text-align: left;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.08);
`;

export const InvoiceForm = styled.form`
  text-align: left;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
`;

/** One logical block in the invoice form (emails, billing, actions). */
export const InvoiceFormSection = styled.section`
  padding: 16px 0;
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};

  &:first-of-type {
    padding-top: 0;
    border-top: none;
  }

  &:last-of-type {
    padding-bottom: 0;
  }
`;

export const InvoiceFormSectionTitle = styled.h3`
  display: block;
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
  line-height: 1.3;
`;

export const InvoiceFormActions = styled(InvoiceFormSection)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const InvoiceSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;

  ${InvoiceFormSectionTitle} {
    margin-bottom: 0;
  }
`;

export const InvoiceSectionIcon = styled.span`
  display: flex;
  font-size: 14px;
  line-height: 1;
`;

export const InvoiceSectionSubtitle = styled.p`
  margin: 0 0 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.BlueBayoux};
  line-height: 1.45;
  text-wrap: pretty;
`;

export const InvoiceOptionalTag = styled.span`
  margin-left: 6px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.baliHai};
`;

/** Alias kept for clarity at call sites that mean “billing block”. */
export const InvoiceBillingSection = InvoiceFormSection;

export const InvoiceFieldGroup = styled.div`
  & + & {
    margin-top: 16px;
  }
`;

export const StripeElementField = styled.div`
  margin-bottom: 0;
`;

export const InvoiceBillingLoading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const InvoiceFieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};
  margin-bottom: 8px;
`;

export const InvoiceHint = styled.p`
  margin: 5px 0 12px;
  font-size: 11px;
  color: ${({ theme }) => theme.color.baliHai};
  line-height: 1.45;

  & + & {
    margin-top: -6px;
  }
`;

export const InvoiceTextInput = styled.input`
  display: block;
  width: 100%;
  flex: 1;
  min-width: 0;
  min-height: 44px;
  padding: 12px;
  font-size: 14px;
  line-height: 1.3;
  border-radius: 10px;
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  color: ${({ theme }) => theme.color.pickledBluewood};
  background: ${({ theme }) => theme.color.white};
  box-sizing: border-box;
  box-shadow: none;

  &::placeholder {
    color: ${({ theme }) => theme.color.baliHai};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.brightTurquoise};
    box-shadow: none;
  }
`;

/** Multi-recipient email input: chips + free-text draft inside one bordered field. */
export const InvoiceChipField = styled.div<{ $focused?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 42px;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1.5px solid
    ${({ theme, $focused }) =>
      $focused ? theme.color.brightTurquoise : theme.color.porcelain};
  background: ${({ theme }) => theme.color.white};
  box-sizing: border-box;
  cursor: text;
`;

export const InvoiceChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  min-width: 0;
  padding: 4px 6px 4px 10px;
  border-radius: 8px;
  background: ${({ theme }) => theme.color.blackSqueeze};
  color: ${({ theme }) => theme.color.pickledBluewood};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
`;

export const InvoiceChipText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const InvoiceChipRemove = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.color.BlueBayoux};
  font-size: 13px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.color.porcelain};
    color: ${({ theme }) => theme.color.pickledBluewood};
  }
`;

export const InvoiceChipInput = styled.input`
  flex: 1;
  min-width: 140px;
  padding: 4px 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${({ theme }) => theme.color.pickledBluewood};

  &::placeholder {
    color: ${({ theme }) => theme.color.baliHai};
  }
`;

export const InvoiceFormError = styled.div`
  font-size: 12px;
  color: #c53030;
  margin: 0;
  line-height: 1.4;
`;

export const InvoiceFieldError = styled(InvoiceFormError)`
  margin-top: 6px;
`;

export const InvoiceSuccessPanel = styled.div`
  text-align: left;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${({ theme }) => theme.color.aquaSqueeze};
  border: 1.5px solid ${({ theme }) => theme.color.brightTurquoise};
  margin-bottom: 16px;
`;

export const InvoiceSuccessTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
  margin-bottom: 8px;
`;

export const InvoiceSuccessAmount = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  margin-bottom: 12px;
`;

export const InvoiceSuccessNote = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  line-height: 1.45;
`;

export const LinkInvoiceHosted = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 16px;
  border-radius: 40px;
  background: ${({ theme }) => theme.color.skobeloffGreen};
  font-size: 14px;
  font-weight: 700;
  color: #fff !important;
  text-decoration: none;
  text-align: center;
  box-sizing: border-box;
  transition: all 0.15s;

  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(10, 117, 117, 0.25);
  }
`;

export const PaymentCard = styled.div`
  background: ${({ theme }) => theme.color.white};
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  border-radius: 20px;
  padding: 24px;
  position: sticky;
  top: 96px;
  box-sizing: border-box;
  min-width: 0;

  @media ${breakpoints.downMdPlus} {
    position: relative;
    top: auto;
  }

  @media ${breakpoints.downSmPlus} {
    padding: 16px;
    border-radius: 16px;
  }
`;

export const PaymentCardTitle = styled.h2`
  font-size: 16px;
  font-weight: bold;
  text-align: left;
  color: ${({ theme }) => theme.color.pickledBluewood};
  margin: 0 0 20px;
`;

export const PriceDisplay = styled.div`
  text-align: center;
  padding: 16px 0 20px;
  border-bottom: 1px solid ${({ theme }) => theme.color.porcelain};
  margin-bottom: 20px;
`;

/** Invoice path - same rhythm as PriceDisplay */
export const InvoicePriceDisplay = styled.div`
  text-align: center;
  padding: 20px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.porcelain};
  margin-bottom: 20px;
`;

export const PriceBig = styled.div`
  font-size: 44px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
  letter-spacing: -2px;
  line-height: 1;
`;

export const PriceBigSup = styled.sup`
  font-size: 22px;
  font-weight: 600;
  margin-right: 5px;
  vertical-align: top;
  margin-top: 8px;
  display: inline-block;
`;

export const PricePeriod = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.color.baliHai};
  letter-spacing: -0.06em;
  margin-left: 2px;
`;

export const PriceSub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.baliHai};
  margin-top: 4px;
`;

export const PriceSave = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.BlueBayoux};
  background: ${({ theme }) => theme.color.koromiko}33;
  padding: 3px 9px;
  border-radius: 40px;
  margin-top: 8px;
`;

export const PriceToday = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  margin-top: 12px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.color.blackSqueeze};
  border-radius: 8px;
  line-height: 1.5;
  text-align: left;

  strong {
    color: ${({ theme }) => theme.color.pickledBluewood};
  }
`;

export const PaySummaryRows = styled.div`
  margin-bottom: 20px;
  text-align: left;
  width: 100%;
`;

export const PaySummaryRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
  font-size: 13px;
  padding: 5px 0;
`;

export const PaySummaryLabelCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  text-align: left;
`;

export const PaySummaryLabel = styled.span`
  color: ${({ theme }) => theme.color.BlueBayoux};
  text-align: left;
`;

export const PaySummarySubtext = styled.span`
  display: block;
  font-size: 10px;
  color: ${({ theme }) => theme.color.baliHai};
  line-height: 1.35;
  text-align: left;
`;

export const PaySummaryVal = styled.span`
  color: ${({ theme }) => theme.color.pickledBluewood};
  font-weight: 500;
  flex-shrink: 0;
  margin-left: auto;
`;

export const PaySummaryValCredit = styled(PaySummaryVal)`
  color: ${({ theme }) => theme.color.brightTurquoise};
  font-weight: 700;
`;

export const PaySummaryTotalRow = styled(PaySummaryRow)`
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
  margin-top: 8px;
  padding-top: 10px;

  ${PaySummaryLabel} {
    font-weight: 600;
    color: ${({ theme }) => theme.color.pickledBluewood};
  }

  ${PaySummaryVal} {
    font-weight: 700;
    font-size: 15px;
    color: ${({ theme }) => theme.color.pickledBluewood};
  }
`;

export const BtnPurchase = styled.button`
  width: 100%;
  padding: 15px;
  height: 56px;
  border-radius: 40px;
  background: ${({ theme }) => theme.color.skobeloffGreen};
  border: none;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  color: #fff;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 10px;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(10, 117, 117, 0.25);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export const BtnPurchaseInner = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 28px;
`;

export const PayActions = styled.div`
  position: relative;
  margin-bottom: 10px;

  iframe {
    border-radius: 40px;
  }

  & > div {
    width: 100%;
  }
`;

export const PayPalShell = styled.div`
  border-radius: 40px;
  overflow: hidden;
  margin-bottom: 10px;
`;

export const StripeNote = styled.div`
  text-align: center;
  font-size: 11px;
  color: ${({ theme }) => theme.color.baliHai};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const Guarantee = styled.div`
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const GuaranteeItem = styled.div`
  display: grid;
  grid-template-columns: 1.4em 1fr;
  align-items: center;
  column-gap: 7px;
  text-align: left;
  font-size: 12px;
  line-height: 1.35;
  color: ${({ theme }) => theme.color.BlueBayoux};
`;

export const GuaranteeIcon = styled.span`
  color: ${({ theme }) => theme.color.brightTurquoise};
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: center;
`;

export const StyledLinkCheckout = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.color.brightTurquoise};
  font-weight: 600;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.color.skobeloffGreen};
  }
`;

export const StyledCancelCheckout = styled.span`
  color: ${({ theme }) => theme.color.baliHai};
  text-decoration: none;
  cursor: pointer;
  display: inline-block;
  margin-top: 10px;
  font-size: 12px;

  &:hover {
    color: ${({ theme }) => theme.color.pickledBluewood};
  }
`;

export const CheckoutPlanMissingWrap = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: 32px 24px;
`;

export const CheckoutPlanMissingText = styled.p`
  margin-bottom: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.BlueBayoux};
`;

export const CheckoutPlanMissingLink = styled(Link)`
  color: ${({ theme }) => theme.color.brightTurquoise};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.color.skobeloffGreen};
  }
`;
